import { Box, Stack, Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import { FlashDesignFormPayload } from '../../../types/flashbooks';
import {
  flashbookCardStyle,
  previewRowStyle,
} from '../../../helpers/styles/flashbook';

const formatStartsAt = (values: FlashDesignFormPayload) => {
  const enabledPrices = values.sizeConfigs
    .filter(c => c.enabled)
    .map(c => parseFloat((c.estimate ?? '').replace(/[^0-9.]/g, '')))
    .filter(n => !Number.isNaN(n) && n > 0);
  if (!enabledPrices.length) return 'No estimate';
  return `$${Math.min(...enabledPrices)}`;
};

const formatSizes = (values: FlashDesignFormPayload) => {
  const count = values.sizeConfigs.filter(c => c.enabled).length;
  if (!count) return 'None selected';
  return `${count} available`;
};

function Row({ label, value }: { label: string; value: string }) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={previewRowStyle}>
      <Typography variant="subtitle2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" color="text.primary">
        {value}
      </Typography>
    </Stack>
  );
}

export default function StorefrontPreviewCard() {
  const { values } = useFormikContext<FlashDesignFormPayload>();

  return (
    <Box
      sx={{
        ...flashbookCardStyle,
        position: 'sticky',
        top: 'var(--sticky-top, 88px)',
      }}>
      <Box
        sx={{
          px: 2.5,
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'darkerLightGrey.main',
        }}>
        <Typography variant="h5">Storefront preview</Typography>
        <Typography
          variant="caption"
          component="p"
          color="text.secondary"
          mt={0.5}>
          How clients will see this flash.
        </Typography>
      </Box>
      <Box
        sx={{
          aspectRatio: '1 / 1',
          bgcolor: 'common.black',
          borderBottom: '1px solid',
          borderColor: 'darkerLightGrey.main',
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        {values.imageUrl ? (
          <Box
            component="img"
            src={values.imageUrl}
            alt={values.name || 'Flash preview'}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        ) : (
          <Typography variant="h5" color="text.disabled">
            Upload a design to preview
          </Typography>
        )}
      </Box>
      <Box sx={{ px: 2.5, py: 2 }}>
        <Row label="Title" value={values.name || '—'} />
        <Row label="Sizes" value={formatSizes(values)} />
        <Row label="Starts at" value={formatStartsAt(values)} />
        <Row label="Repeatable" value={values.isRepeatable ? 'Yes' : 'No'} />
        <Row
          label="Direct booking"
          value={values.enableDirectBookings ? 'Enabled' : 'Disabled'}
        />
      </Box>
    </Box>
  );
}
